import React, { useState, useCallback, useEffect } from 'react';
import { Sandbox } from '../Sandbox';
import { PuzzleService, formatPuzzleDisplayTitle, type Puzzle } from '../../services/puzzleService';
import type { Cell } from '../../game/types';
import { copyBoard } from '../../game/logic/board';
import './PuzzleCreator.css';

interface PuzzleCreatorProps {
  onPuzzleCreated: () => void;
  onBack: () => void;
  authState?: {
    isAuthenticated: boolean;
    isGuest?: boolean;
    user: { id: string; username: string; email: string } | null;
  };
}

type TabType = 'create' | 'my-puzzles';

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'status-draft' },
  pending_review: { label: 'Pending Review', className: 'status-pending' },
  approved: { label: 'Approved', className: 'status-approved' },
  rejected: { label: 'Not Approved', className: 'status-rejected' },
};

const PuzzleCreator: React.FC<PuzzleCreatorProps> = ({ onPuzzleCreated, onBack, authState }) => {
  const isMember = authState?.isAuthenticated && !authState?.isGuest;

  const [activeTab, setActiveTab] = useState<TabType>('create');
  const [editingPuzzleId, setEditingPuzzleId] = useState<number | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard' | 'expert' | 'insane'>('medium');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Board state from Sandbox
  const [currentBoard, setCurrentBoard] = useState<(Cell | null)[][] | null>(null);
  const [currentStartingPlayer, setCurrentStartingPlayer] = useState<'white' | 'black'>('white');
  const [currentSolutionMoves, setCurrentSolutionMoves] = useState<Array<{ row: number; col: number; player: 'white' | 'black' }>>([]);

  // Solution recording
  const [isRecordingSolution, setIsRecordingSolution] = useState(false);

  // My puzzles list
  const [myPuzzles, setMyPuzzles] = useState<Puzzle[]>([]);
  const [loadingPuzzles, setLoadingPuzzles] = useState(false);
  const [puzzleError, setPuzzleError] = useState<string | null>(null);

  // Submission thank-you
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);

  const loadMyPuzzles = useCallback(async () => {
    if (!isMember) return;
    setLoadingPuzzles(true);
    setPuzzleError(null);
    try {
      const result = await PuzzleService.getMyPuzzles();
      if (result.success) {
        setMyPuzzles(result.puzzles);
      } else {
        setPuzzleError(result.error || 'Failed to load your puzzles');
      }
    } catch (err: any) {
      setPuzzleError(err.message || 'Failed to load your puzzles');
    } finally {
      setLoadingPuzzles(false);
    }
  }, [isMember]);

  useEffect(() => {
    if (activeTab === 'my-puzzles') loadMyPuzzles();
  }, [activeTab, loadMyPuzzles]);

  // Board state callbacks
  const handleBoardStateUpdate = useCallback((board: (Cell | null)[][], startingPlayer: 'white' | 'black') => {
    setCurrentBoard(board);
    setCurrentStartingPlayer(startingPlayer);
  }, []);

  const handleSandboxStartGame = useCallback((board: (Cell | null)[][], startingPlayer: 'white' | 'black') => {
    setCurrentBoard(board);
    setCurrentStartingPlayer(startingPlayer);
  }, []);

  const handleMoveRecorded = useCallback((move: { row: number; col: number; player: 'white' | 'black' }) => {
    if (isRecordingSolution) {
      setCurrentSolutionMoves(prev => [...prev, move]);
    }
  }, [isRecordingSolution]);

  const handleToggleRecording = useCallback(() => {
    if (!isRecordingSolution) {
      setCurrentSolutionMoves([]);
      setIsRecordingSolution(true);
      setError(null);
    } else {
      setIsRecordingSolution(false);
    }
  }, [isRecordingSolution]);

  const handleClearSolution = useCallback(() => {
    if (window.confirm('Clear the recorded solution?')) {
      setCurrentSolutionMoves([]);
      setIsRecordingSolution(false);
    }
  }, []);

  const handleRemoveMove = useCallback((index: number) => {
    setCurrentSolutionMoves(prev => prev.filter((_, i) => i !== index));
  }, []);

  const sqToAlgebraic = (row: number, col: number) =>
    `${String.fromCharCode(97 + col)}${8 - row}`;

  // ── Save draft ──
  const handleSaveDraft = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    if (!currentBoard) { setError('Please set up a board position first'); return; }
    let hasPiece = false;
    for (let r = 0; r < 8 && !hasPiece; r++)
      for (let c = 0; c < 8 && !hasPiece; c++)
        if (currentBoard[r][c]) hasPiece = true;
    if (!hasPiece) { setError('Please place at least one piece on the board'); return; }

    setSaving(true);
    setError(null);
    try {
      let result;
      if (editingPuzzleId) {
        result = await PuzzleService.updatePuzzle(editingPuzzleId, {
          title: title.trim(),
          description: description.trim() || undefined,
          difficulty,
          initialBoard: currentBoard,
          startingPlayer: currentStartingPlayer,
          solutionMoves: currentSolutionMoves.length > 0 ? currentSolutionMoves : undefined,
        });
      } else {
        result = await PuzzleService.createPuzzle({
          title: title.trim(),
          description: description.trim() || undefined,
          difficulty,
          initialBoard: currentBoard,
          startingPlayer: currentStartingPlayer,
          solutionMoves: currentSolutionMoves.length > 0 ? currentSolutionMoves : undefined,
          status: 'draft',
        });
      }
      if (result.success) {
        setSuccess(editingPuzzleId ? 'Draft updated!' : 'Draft saved!');
        if (!editingPuzzleId && result.puzzle?.id) setEditingPuzzleId(result.puzzle.id);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.error || 'Failed to save draft');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  // ── Submit for review ──
  const handleSubmitForReview = async (puzzleId?: number) => {
    const id = puzzleId || editingPuzzleId;

    if (!id) {
      if (!title.trim()) { setError('Title is required'); return; }
      if (!currentBoard) { setError('Please set up a board position first'); return; }
      setSaving(true);
      setError(null);
      try {
        const createResult = await PuzzleService.createPuzzle({
          title: title.trim(),
          description: description.trim() || undefined,
          difficulty,
          initialBoard: currentBoard,
          startingPlayer: currentStartingPlayer,
          solutionMoves: currentSolutionMoves.length > 0 ? currentSolutionMoves : undefined,
          status: 'draft',
        });
        if (!createResult.success || !createResult.puzzle?.id) {
          setError(createResult.error || 'Failed to create puzzle');
          setSaving(false);
          return;
        }
        const newId = createResult.puzzle.id;
        setEditingPuzzleId(newId);
        const submitResult = await PuzzleService.submitPuzzleForReview(newId);
        if (submitResult.success) {
          setShowSubmitSuccess(true);
          resetForm();
        } else {
          setError(submitResult.error || 'Failed to submit');
        }
      } catch (err: any) {
        setError(err.message || 'An error occurred');
      } finally {
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (editingPuzzleId && currentBoard) {
        await PuzzleService.updatePuzzle(editingPuzzleId, {
          title: title.trim(),
          description: description.trim() || undefined,
          difficulty,
          initialBoard: currentBoard,
          startingPlayer: currentStartingPlayer,
          solutionMoves: currentSolutionMoves.length > 0 ? currentSolutionMoves : undefined,
        });
      }
      const result = await PuzzleService.submitPuzzleForReview(id);
      if (result.success) {
        setShowSubmitSuccess(true);
        resetForm();
        if (activeTab === 'my-puzzles') loadMyPuzzles();
      } else {
        setError(result.error || 'Failed to submit');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setDifficulty('medium');
    setCurrentBoard(null);
    setCurrentStartingPlayer('white');
    setCurrentSolutionMoves([]);
    setIsRecordingSolution(false);
    setEditingPuzzleId(null);
    setError(null);
  };

  const handleEditPuzzle = useCallback(async (puzzle: Puzzle) => {
    setEditingPuzzleId(puzzle.id);
    setTitle(puzzle.title);
    setDescription(puzzle.description || '');
    setDifficulty(puzzle.difficulty);
    setCurrentBoard(copyBoard(puzzle.initial_board));
    setCurrentStartingPlayer(puzzle.starting_player);
    setCurrentSolutionMoves(puzzle.solution_moves || []);
    setIsRecordingSolution(false);
    setActiveTab('create');
  }, []);

  const handleDeletePuzzle = useCallback(async (puzzleId: number) => {
    if (!window.confirm('Delete this puzzle draft? This cannot be undone.')) return;
    try {
      const result = await PuzzleService.deletePuzzle(puzzleId);
      if (result.success) loadMyPuzzles();
      else setPuzzleError(result.error || 'Failed to delete');
    } catch (err: any) {
      setPuzzleError(err.message || 'Failed to delete');
    }
  }, [loadMyPuzzles]);

  // ── Not a member ──
  if (!isMember) {
    return (
      <div className="puzzle-creator">
        <div className="puzzle-creator-header">
          <h2>Puzzle Test Lab</h2>
        </div>
        <div className="puzzles-view-placeholder">
          <p>Sign in as a member to create and submit puzzles.</p>
        </div>
      </div>
    );
  }

  // ── Submit success overlay ──
  if (showSubmitSuccess) {
    return (
      <div className="puzzle-creator">
        <div className="puzzle-submit-success">
          <div className="puzzle-submit-success-inner">
            <h2>Thank you for creating a puzzle!</h2>
            <p>
              Your puzzle has been submitted for testing and review.
              If approved, it will be added to the puzzle list for all players to enjoy.
            </p>
            <p className="puzzle-submit-note">
              You can check the status of your submissions in the <strong>My Puzzles</strong> tab.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => {
                setShowSubmitSuccess(false);
                setActiveTab('my-puzzles');
                loadMyPuzzles();
              }}
            >
              View My Puzzles
            </button>
            <button
              className="btn btn-secondary"
              style={{ marginLeft: '10px' }}
              onClick={() => {
                setShowSubmitSuccess(false);
                resetForm();
              }}
            >
              Create Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="puzzle-creator">
      <div className="puzzle-creator-header">
        <h2>{editingPuzzleId ? `Edit Puzzle #${editingPuzzleId}` : 'Puzzle Test Lab'}</h2>
        <div className="puzzle-creator-tabs">
          <button
            className={`puzzles-tab ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            {editingPuzzleId ? 'Edit' : 'Create'}
          </button>
          <button
            className={`puzzles-tab ${activeTab === 'my-puzzles' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-puzzles')}
          >
            My Puzzles
          </button>
        </div>
      </div>

      {activeTab === 'my-puzzles' ? (
        <div className="puzzle-my-list">
          {loadingPuzzles ? (
            <div className="puzzle-loading">Loading your puzzles...</div>
          ) : puzzleError ? (
            <div className="form-error">{puzzleError}</div>
          ) : myPuzzles.length === 0 ? (
            <div className="puzzle-loading">You haven't created any puzzles yet. Switch to the Create tab to get started!</div>
          ) : (
            <div className="puzzle-my-items">
              {myPuzzles.map(puzzle => {
                const st = STATUS_LABELS[puzzle.status || 'draft'] || STATUS_LABELS.draft;
                return (
                  <div key={puzzle.id} className="puzzle-my-item">
                    <div className="puzzle-my-item-info">
                      <div className="puzzle-my-item-title">
                        <strong>{formatPuzzleDisplayTitle(puzzle)}</strong>
                        <span className={`puzzle-status-badge ${st.className}`}>{st.label}</span>
                      </div>
                      <div className="puzzle-my-item-meta">
                        <span className={`difficulty difficulty-${puzzle.difficulty}`}>{puzzle.difficulty}</span>
                        <span>{puzzle.solution_moves ? `${puzzle.solution_moves.length} moves` : 'No solution'}</span>
                      </div>
                      {puzzle.review_message && (
                        <div className={`puzzle-review-message ${puzzle.status === 'approved' ? 'approved' : 'rejected'}`}>
                          {puzzle.review_message}
                        </div>
                      )}
                    </div>
                    <div className="puzzle-my-item-actions">
                      {(puzzle.status === 'draft' || puzzle.status === 'rejected') && (
                        <>
                          <button className="btn btn-small" onClick={() => handleEditPuzzle(puzzle)}>Edit</button>
                          <button
                            className="btn btn-primary btn-small"
                            onClick={() => handleSubmitForReview(puzzle.id)}
                            disabled={saving}
                          >
                            Submit
                          </button>
                        </>
                      )}
                      {puzzle.status === 'pending_review' && (
                        <span className="puzzle-status-note">Awaiting review</span>
                      )}
                      {(puzzle.status === 'draft' || puzzle.status === 'rejected') && (
                        <button className="btn btn-danger btn-small" onClick={() => handleDeletePuzzle(puzzle.id)}>Delete</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="puzzle-creator-content">
          <div className="puzzle-creator-form">
            <div className="form-group">
              <label htmlFor="puzzle-title">Title *</label>
              <input
                id="puzzle-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter puzzle title"
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label htmlFor="puzzle-description">Description</label>
              <textarea
                id="puzzle-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the puzzle or give a hint (optional)"
                rows={2}
                maxLength={500}
              />
            </div>

            <div className="form-group">
              <label htmlFor="puzzle-difficulty">Difficulty *</label>
              <select
                id="puzzle-difficulty"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as 'easy' | 'medium' | 'hard' | 'expert' | 'insane')}
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="expert">Expert</option>
                <option value="insane">Insane</option>
              </select>
            </div>

            {error && <div className="form-error">{error}</div>}
            {success && <div className="form-success">{success}</div>}

            {/* Solution recording */}
            <div className="form-group solution-controls">
              <label>Solution Moves</label>
              <div className="solution-buttons">
                <button
                  type="button"
                  onClick={handleToggleRecording}
                  className={`record-solution-btn ${isRecordingSolution ? 'recording' : ''}`}
                >
                  {isRecordingSolution ? 'Stop Recording' : 'Record Solution'}
                </button>
                <button
                  type="button"
                  onClick={handleClearSolution}
                  disabled={currentSolutionMoves.length === 0}
                  className="clear-solution-btn"
                >
                  Clear
                </button>
              </div>
              {currentSolutionMoves.length > 0 && (
                <div className="solution-moves-list">
                  <div className="solution-moves-header">
                    {currentSolutionMoves.length} move{currentSolutionMoves.length !== 1 ? 's' : ''} recorded
                  </div>
                  <div className="solution-moves-items">
                    {currentSolutionMoves.map((move, index) => (
                      <div key={index} className="solution-move-item">
                        <span className="move-number">{index + 1}.</span>
                        <span className="move-player">{move.player === 'white' ? 'White' : 'Black'}</span>
                        <span className="move-position">{sqToAlgebraic(move.row, move.col)}</span>
                        <button type="button" onClick={() => handleRemoveMove(index)} className="remove-move-btn" title="Remove">x</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {isRecordingSolution && (
                <div className="recording-indicator">
                  <span className="recording-dot"></span>
                  Recording solution moves...
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="form-actions-row">
              {editingPuzzleId && (
                <button onClick={resetForm} className="btn btn-secondary" style={{ flex: 1 }}>
                  Cancel Edit
                </button>
              )}
              <button
                onClick={handleSaveDraft}
                disabled={saving || !title.trim()}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={() => handleSubmitForReview()}
                disabled={saving || !title.trim()}
                className="btn btn-primary"
                style={{ flex: 1 }}
              >
                {saving ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>

          </div>

          <div className="puzzle-creator-sandbox">
            <Sandbox
              onBoardStateChange={handleBoardStateUpdate}
              onStartGame={handleSandboxStartGame}
              onBack={onBack}
              onMoveRecorded={handleMoveRecorded}
              isRecordingSolution={isRecordingSolution}
              initialBoard={currentBoard || undefined}
              initialStartingPlayer={currentStartingPlayer}
              showEngineToggle={true}
              authState={authState}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PuzzleCreator;
